import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Icon, Span } from "@icg360/design-system";

import { Stack } from "components/common/stack";
import { MultiStepFormLayout } from "components/multi-step-form-layout";
import { useSelectedPolicy } from "data/ss-data-hooks";
import { useMultiStepFormState } from "hooks/use-multi-step-form-state";
import {
  useWindDefenseEnrollmentSteps,
  useMultiWindDefenseEnrollment,
} from "pages/offer/wind-defense/hooks";
import {
  WindDefenseContext,
  type WindDefenseContextState,
} from "pages/offer/wind-defense/wind-defense-flow";
import { formatTwoLineAddress, trackEvent } from "utils";

import { windDefensePolicyCardUiIcon } from "./policy-helpers";
import styles from "./shingle-reseal.module.scss";

export const ReviewEnroll = () => {
  const { data: selectedPolicy } = useSelectedPolicy();
  const navigate = useNavigate();
  const { state, formNavigate } =
    useMultiStepFormState<WindDefenseContextState>(WindDefenseContext);

  const { stepLabels, totalSteps, step } = useWindDefenseEnrollmentSteps(state);
  const { enrollWindDefense, loading } = useMultiWindDefenseEnrollment(state);
  const [submissionError, setSubmissionError] = useState<string[]>([]);
  const [submissionSuccess, setSubmissionSuccess] = useState<string[]>([]);

  const policiesToEnroll = useMemo(
    () =>
      state?.eligibleWindDefensePolicies?.filter((p) =>
        (
          state?.selectedWindDefensePolicyIds || [
            selectedPolicy?.keystonePolicyId,
          ]
        ).includes(p.keystonePolicyId),
      ) ?? [],
    [
      state?.eligibleWindDefensePolicies,
      state?.selectedWindDefensePolicyIds,
      selectedPolicy?.keystonePolicyId,
    ],
  );

  useEffect(() => {
    if (
      !(
        state?.phone &&
        state?.firstName &&
        state?.lastName &&
        state?.emailAddress
      )
    ) {
      navigate("../policy-confirmation");
      return;
    }
  }, [
    navigate,
    policiesToEnroll.length,
    state?.emailAddress,
    state?.firstName,
    state?.lastName,
    state?.phone,
  ]);

  useEffect(() => {
    if (submissionSuccess.length === policiesToEnroll.length) {
      trackEvent("Wind Defense Reduce Flow: Enrollment Success", {
        policiesToEnrollCount: policiesToEnroll.length,
        policiesToEnrollIds: policiesToEnroll.map((p) => p.keystonePolicyId),
        submissionSuccess: submissionSuccess.join(", "),
      });
      navigate("/my/property-services/wind-defense/registered");
    }

    if (submissionError.length > 0) {
      trackEvent("Wind Defense Reduce Flow: Enrollment Error", {
        policiesToEnrollCount: policiesToEnroll.length,
        policiesToEnrollIds: policiesToEnroll.map((p) => p.keystonePolicyId),
        submissionError: submissionError.join(", "),
      });
      throw new Error(
        "error signing up for Wind Defense for policies: " +
          submissionError.join(", "),
      );
    }
  }, [submissionSuccess, policiesToEnroll, navigate, submissionError]);

  const onSubmit = async () => {
    trackEvent("Wind Defense Reduce Flow: Submit Enrollment Clicked", {
      policiesToEnrollCount: policiesToEnroll.length,
      policiesToEnrollIds: policiesToEnroll.map((p) => p.keystonePolicyId),
    });
    for (const policy of policiesToEnroll) {
      const success = await enrollWindDefense({
        eligibilityInfo: policy.eligibilityDetails,
      });
      if (success) {
        setSubmissionSuccess((prev) => [...prev, policy.keystonePolicyId]);
      } else {
        setSubmissionError((prev) => [...prev, policy.keystonePolicyId]);
      }
    }
  };

  return (
    <MultiStepFormLayout
      showLogo
      labels={stepLabels}
      step={step}
      totalSteps={totalSteps}
      heading="Review details and finish enrollment"
      buttonProps={{
        primary: { text: "Finish", onPress: onSubmit, loading: loading },
        cancel: { path: "/my/property-services/wind-defense" },
        promptOnCancel: true,
        formNavigate,
      }}
    >
      <Stack>
        <div className={styles.iconItemSm}>
          <div className={styles.iconDS}>
            <Icon name="User" size="lg" />
          </div>
          <div>
            <Span bold>
              {state?.firstName} {state?.lastName}
            </Span>
            <div className={styles.subtext}>{state?.emailAddress}</div>
            <div className={styles.subtext}>{state?.phone}</div>
          </div>
        </div>
        {policiesToEnroll.map((policy) => {
          const { lineOne, lineTwo } = formatTwoLineAddress(
            policy.propertyAddress ?? {},
            true,
          );
          const propertyIcon = windDefensePolicyCardUiIcon(policy);
          return (
            <div className={styles.iconItemSm} key={policy.keystonePolicyId}>
              <div className={styles.iconDS}>
                <Icon name={propertyIcon} size="lg" />
              </div>
              <div>
                <Span bold>{lineOne}</Span>
                <br />
                <Span bold>{lineTwo}</Span>
                <div className={styles.subtext}>
                  Policy number: {policy.platformPolicyId}
                </div>
              </div>
            </div>
          );
        })}
      </Stack>
    </MultiStepFormLayout>
  );
};
